import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Pagination,
  Table,
  Button,
  TextInput,
  Textarea,
  Checkbox,
  ActionIcon,
  Loader,
  Text,
  Select,
  Badge,
  Group,
  Paper,
  Title,
  Stack,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  useListAllSettingsQuery,
  useUpsertSettingMutation,
  SettingDataType,
  SettingVisibility,
} from "generated/graphql";
import {
  IconEdit,
  IconCheck,
  IconX,
  IconTrash,
  IconPlus,
  IconArrowLeft,
  IconSearch,
  IconRefresh,
} from "@tabler/icons-react";
import { SettingCreateModal, SettingDeleteConfirmModal } from "components/Settings";
import { HasAction, useHasAction } from "components/Auth/HasAction";

export default function SystemSettingsRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SettingsManagement />} />
    </Routes>
  );
}

const PAGE_SIZE = 15;

const VISIBILITY_COLORS: Record<string, string> = {
  PUBLIC: "green",
  PROTECTED: "blue",
  INTERNAL: "orange",
  SYSTEM: "gray",
};

const DATA_TYPE_OPTIONS = [
  { value: "STRING", label: "String" },
  { value: "BOOLEAN", label: "Boolean" },
  { value: "INTEGER", label: "Integer" },
  { value: "DECIMAL", label: "Decimal" },
  { value: "DATE_TIME", label: "DateTime" },
  { value: "JSON", label: "JSON" },
  { value: "STRING_LIST", label: "String List" },
];

const VISIBILITY_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "PROTECTED", label: "Protected" },
  { value: "INTERNAL", label: "Internal" },
  { value: "SYSTEM", label: "System" },
];

interface EditingState {
  value: string;
  dataType: string;
  visibility: string;
  description: string;
}

function SettingsManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Permission check
  const canEditSettings = useHasAction("settings:write");

  // State
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetPath, setDeleteTargetPath] = useState<string | null>(null);

  // Queries & Mutations
  const { data, loading, error, refetch } = useListAllSettingsQuery({
    variables: {
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  const [upsertSetting, { loading: saving }] = useUpsertSettingMutation();

  // Filter settings by search query (client-side for current page)
  const filteredSettings = useMemo(() => {
    const items = data?.listAllSettings?.items ?? [];
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(
      (s) =>
        s?.path?.toLowerCase().includes(query) ||
        s?.value?.toLowerCase().includes(query) ||
        s?.description?.toLowerCase().includes(query)
    );
  }, [data, searchQuery]);

  const totalPages = Math.ceil((data?.listAllSettings?.totalCount || 0) / PAGE_SIZE);

  // Editing handlers
  const startEditing = useCallback((path: string, setting: any) => {
    setEditingPath(path);
    setEditingState({
      value: setting.value ?? "",
      dataType: setting.dataType ?? "STRING",
      visibility: setting.visibility ?? "INTERNAL",
      description: setting.description ?? "",
    });
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingPath(null);
    setEditingState(null);
  }, []);

  const saveEditing = useCallback(async () => {
    if (!editingPath || !editingState) return;

    // Validate value based on data type
    const validationError = validateValue(editingState.value, editingState.dataType);
    if (validationError) {
      notifications.show({
        title: "Validation Error",
        message: validationError,
        color: "red",
      });
      return;
    }

    try {
      await upsertSetting({
        variables: {
          input: {
            path: editingPath,
            value: editingState.value,
            dataType: editingState.dataType as SettingDataType,
            visibility: editingState.visibility as SettingVisibility,
            description: editingState.description || undefined,
          },
        },
      });

      notifications.show({
        title: "Setting Updated",
        message: `Setting "${editingPath}" has been updated.`,
        color: "green",
      });

      cancelEditing();
      refetch();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to update setting",
        color: "red",
      });
    }
  }, [editingPath, editingState, upsertSetting, cancelEditing, refetch]);

  const handleDelete = useCallback((path: string) => {
    setDeleteTargetPath(path);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleCreateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Render helpers
  const renderValue = (setting: any, isEditing: boolean) => {
    const dataType = setting.dataType?.toUpperCase() ?? "STRING";

    if (isEditing && editingState) {
      if (dataType === "BOOLEAN") {
        return (
          <Checkbox
            checked={editingState.value.toLowerCase() === "true"}
            onChange={(e) =>
              setEditingState((prev) =>
                prev ? { ...prev, value: e.currentTarget.checked ? "true" : "false" } : null
              )
            }
          />
        );
      }
      if (dataType === "JSON" || dataType === "STRING_LIST") {
        return (
          <Textarea
            value={editingState.value}
            onChange={(e) =>
              setEditingState((prev) =>
                prev ? { ...prev, value: e.currentTarget.value } : null
              )
            }
            minRows={2}
            size="sm"
            style={{ minWidth: "200px" }}
          />
        );
      }
      return (
        <TextInput
          value={editingState.value}
          onChange={(e) =>
            setEditingState((prev) =>
              prev ? { ...prev, value: e.currentTarget.value } : null
            )
          }
          size="sm"
          style={{ minWidth: "200px" }}
        />
      );
    }

    // Display mode
    if (dataType === "BOOLEAN") {
      return (
        <Checkbox
          checked={setting.value?.toLowerCase() === "true"}
          readOnly
          style={{ cursor: "default" }}
        />
      );
    }

    const displayValue = setting.value ?? "";
    if (displayValue.length > 50) {
      return (
        <Tooltip label={displayValue} multiline w={300}>
          <Text size="sm" truncate style={{ maxWidth: "200px" }}>
            {displayValue}
          </Text>
        </Tooltip>
      );
    }
    return <Text size="sm">{displayValue}</Text>;
  };

  const renderVisibility = (setting: any, isEditing: boolean) => {
    const visibility = setting.visibility ?? "INTERNAL";

    if (isEditing && editingState) {
      return (
        <Select
          value={editingState.visibility}
          onChange={(v) =>
            setEditingState((prev) =>
              prev ? { ...prev, visibility: v ?? "INTERNAL" } : null
            )
          }
          data={VISIBILITY_OPTIONS}
          size="sm"
          style={{ minWidth: "120px" }}
        />
      );
    }

    return (
      <Badge color={VISIBILITY_COLORS[visibility] || "gray"} variant="light">
        {visibility}
      </Badge>
    );
  };

  const renderDataType = (setting: any, isEditing: boolean) => {
    const dataType = setting.dataType ?? "STRING";

    if (isEditing && editingState) {
      return (
        <Select
          value={editingState.dataType}
          onChange={(v) =>
            setEditingState((prev) =>
              prev ? { ...prev, dataType: v ?? "STRING" } : null
            )
          }
          data={DATA_TYPE_OPTIONS}
          size="sm"
          style={{ minWidth: "120px" }}
        />
      );
    }

    return <Text size="sm">{dataType}</Text>;
  };

  // Loading state
  if (loading && !data) {
    return (
      <Container size="xl" px="lg" py="lg">
        <div className="flex justify-center items-center h-64">
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container size="xl" px="lg" py="lg">
        <Paper withBorder p="lg" radius="md">
          <Text c="red">Error loading settings: {error.message}</Text>
          <Button mt="md" onClick={() => refetch()}>
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl" px="lg" py="lg">
      {/* Header */}
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub")}
        >
          Back to Admin Hub
        </Button>
      </Group>

      <Stack gap="md">
        <Title c={isDark ? "white" : "black"}>Settings Management</Title>
        <Text c={isDark ? "gray.4" : "gray.7"}>
          Manage application settings and feature flags. Settings are organized by path
          and can have different visibility levels.
        </Text>

        {/* Actions Bar */}
        <Group justify="space-between" wrap="wrap">
          <TextInput
            placeholder="Search by path, value, or description..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            className="min-w-[300px]"
          />
          <Group>
            <Button
              variant="light"
              color="gray"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
            <HasAction action="settings:write">
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setCreateModalOpen(true)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Create Setting
              </Button>
            </HasAction>
          </Group>
        </Group>

        {/* Settings Table */}
        <Paper withBorder radius="md" shadow="sm">
          <Table.ScrollContainer minWidth={900}>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Path</Table.Th>
                  <Table.Th w={120}>Data Type</Table.Th>
                  <Table.Th>Value</Table.Th>
                  <Table.Th w={120}>Visibility</Table.Th>
                  <Table.Th w={120}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredSettings.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="xl">
                        No settings found. {canEditSettings && "Create one to get started."}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredSettings.map((setting) => {
                    if (!setting) return null;
                    const isEditing = editingPath === setting.path;
                    const isSystemVisibility = setting.visibility === "SYSTEM";

                    return (
                      <Table.Tr key={setting.path}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {setting.path}
                          </Text>
                          {setting.description && (
                            <Text size="xs" c="dimmed">
                              {setting.description}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{renderDataType(setting, isEditing)}</Table.Td>
                        <Table.Td>{renderValue(setting, isEditing)}</Table.Td>
                        <Table.Td>{renderVisibility(setting, isEditing)}</Table.Td>
                        <Table.Td>
                          {isEditing ? (
                            <Group gap={4}>
                              <ActionIcon
                                color="green"
                                variant="filled"
                                size="sm"
                                onClick={saveEditing}
                                loading={saving}
                                title="Save"
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                              <ActionIcon
                                color="red"
                                variant="filled"
                                size="sm"
                                onClick={cancelEditing}
                                title="Cancel"
                              >
                                <IconX size={16} />
                              </ActionIcon>
                            </Group>
                          ) : (
                            <Group gap={4}>
                              {canEditSettings && !isSystemVisibility && (
                                <>
                                  <Tooltip label="Edit setting">
                                    <ActionIcon
                                      variant="subtle"
                                      size="sm"
                                      onClick={() => startEditing(setting.path, setting)}
                                      title="Edit"
                                    >
                                      <IconEdit size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Delete setting">
                                    <ActionIcon
                                      variant="subtle"
                                      color="red"
                                      size="sm"
                                      onClick={() => handleDelete(setting.path)}
                                      title="Delete"
                                    >
                                      <IconTrash size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              )}
                              {isSystemVisibility && (
                                <Text size="xs" c="dimmed">
                                  Read-only
                                </Text>
                              )}
                            </Group>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {/* Pagination */}
        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              total={totalPages}
              value={page}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </Group>
        )}
      </Stack>

      {/* Modals */}
      <SettingCreateModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
      <SettingDeleteConfirmModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
        settingPath={deleteTargetPath}
      />
    </Container>
  );
}

// Validation helper
function validateValue(value: string, dataType: string): string | null {
  switch (dataType.toUpperCase()) {
    case "BOOLEAN":
      if (!["true", "false"].includes(value.toLowerCase())) {
        return 'Boolean must be "true" or "false"';
      }
      break;
    case "INTEGER":
      if (!/^-?\d+$/.test(value)) {
        return "Integer must be a whole number";
      }
      break;
    case "DECIMAL":
      if (!/^-?\d+(\.\d+)?$/.test(value)) {
        return "Decimal must be a valid number";
      }
      break;
    case "JSON":
      try {
        JSON.parse(value);
      } catch {
        return "Invalid JSON format";
      }
      break;
    case "STRING_LIST":
      try {
        const arr = JSON.parse(value);
        if (!Array.isArray(arr) || !arr.every((item) => typeof item === "string")) {
          return "Must be a JSON array of strings";
        }
      } catch {
        return "Invalid JSON array format";
      }
      break;
  }
  return null;
}
